<template>
  <div>
    <NavigationMobileTop v-if="isMessagesRoute" :links="messagesMobileLinks" />
    <div :class="{ 'max-lg:hidden': isMessagesRoute }">
      <header class="global-navigation flex justify-between">
        <NavigationLogo :to="preventRedirectInRegistration ? '' : '/account/seekers/dashboard'" />
        <router-link
          v-if="!basicAccount && !isSimpleHeader"
          to="/messages"
          class="flex mobile-messaging-icon mr-16"
          tabindex="0"
          :aria-current="activeIcon === 'messages' && 'page'"
          aria-label="Messages"
          role="link"
        >
          <messages-icon-outline />
          <badge v-if="unreadMessageCount > 0" :number="unreadMessageCount" />
        </router-link>
        <button
          v-else-if="isSimpleHeader"
          id="mobile-account-menu-btn"
          class="flex flex-row justify-center items-center md-1:hidden h-full mr-16"
          tabindex="0"
          :aria-expanded="isModalOpen ? 'true' : 'false'"
          aria-haspopup="global-nav-dropdown"
          @click="toggleModal"
        >
          <Avatar :name="`${account.firstName} ${account.lastName}`" :imageUrl="avatarUrl" />
          <div class="text-caption -mb-8 ml-6">
            <span class="text-caption truncate-name">{{ account.firstName }}</span>
            <caret-icon class="inline mb-8" :class="isModalOpen && 'upside-down-icon'" />
          </div>
        </button>
        <Button
          v-else
          class="mr-16 md-1:hidden"
          value="Upgrade"
          variant="primary"
          href="/upgrade/pricing"
          size="small"
          @click="upgradeClicked"
        />
        <nav class="flex h-full items-center mr-24 icon-container hidden" aria-label="Primary Navigation">
          <NavigationItem
            v-if="!isSimpleHeader"
            text="Home"
            to="/account/seekers/dashboard"
            :isActive="activeIcon === 'home'"
            :aria-current="activeIcon === 'home' && 'page'"
          >
            <home-icon-filled v-if="activeIcon === 'home'" />
            <home-icon-outline v-else />
          </NavigationItem>
          <NavigationItem
            v-if="!isSimpleHeader"
            text="Jobs"
            to="/seeker/jobs"
            :badgeNum="receivedAppsCount"
            :isActive="activeIcon === 'jobs'"
            :aria-current="activeIcon === 'jobs' && 'page'"
          >
            <jobs-icon-filled v-if="activeIcon === 'jobs'" />
            <jobs-icon-outline v-else />
          </NavigationItem>
          <NavigationItem
            v-if="!isSimpleHeader"
            text="Explore"
            to="/availability/search"
            :isActive="activeIcon === 'explore'"
            :aria-current="activeIcon === 'explore' && 'page'"
          >
            <magnifying-glass-icon-filled v-if="activeIcon === 'explore'" />
            <magnifying-glass-icon-outline v-else />
          </NavigationItem>
          <NavigationItem
            v-if="!basicAccount && !isSimpleHeader"
            text="Messages"
            to="/messages"
            :badgeNum="unreadMessageCount"
            :isActive="activeIcon === 'messages'"
            :aria-current="activeIcon === 'messages' && 'page'"
          >
            <messages-icon-filled v-if="activeIcon === 'messages'" />
            <messages-icon-outline v-else />
          </NavigationItem>
          <NavigationItem
            v-if="!basicAccount && !isSimpleHeader"
            text="Bookings"
            to="/bookings"
            :isActive="activeIcon === 'bookings'"
            :aria-current="activeIcon === 'bookings' && 'page'"
          >
            <bookings-icon-filled v-if="activeIcon === 'bookings'" />
            <bookings-icon-outline v-else />
          </NavigationItem>
          <button
            id="desktop-account-menu-btn"
            class="nav-icon-group h-full justify-center flex flex-col items-center"
            tabindex="0"
            :aria-expanded="isModalOpen ? 'true' : 'false'"
            aria-haspopup="global-nav-dropdown"
            :aria-label="`Account Menu. You are logged in as ${account.firstName}.`"
            @click="toggleModal"
          >
            <Avatar :name="`${account.firstName} ${account.lastName}`" :imageUrl="avatarUrl" />
            <div class="text-caption -mb-8">
              <span id="desktop-account-menu-name" class="text-caption truncate-name">{{ account.firstName }}</span>
              <caret-icon class="inline mb-8" :class="isModalOpen && 'upside-down-icon'" aria-hidden="true" />
            </div>
          </button>
          <Button
            v-if="!isSimpleHeader && basicAccount"
            value="Upgrade"
            variant="primary"
            href="/upgrade/pricing"
            size="medium"
            @click="upgradeClicked"
          />
        </nav>
      </header>
      <global-nav-dropdown
        v-show="isModalOpen"
        :firstName="account.firstName"
        :lastName="account.lastName"
        :profileImage="avatarUrl"
        :accountType="accountType"
        :isSimpleHeader="isSimpleHeader"
        @handleClickOutside="handleClickOutside"
      />
      <nav v-if="!isSimpleHeader" id="mobile-nav-bar" class="flex h-full items-center icon-container-mobile-bottom">
        <NavigationItem
          text="Home"
          to="/account/seekers/dashboard"
          :isActive="activeIcon === 'home'"
          :isMobile="true"
          :aria-current="activeIcon === 'home' && 'page'"
        >
          <home-icon-filled v-if="activeIcon === 'home'" />
          <home-icon-outline v-else />
        </NavigationItem>
        <NavigationItem
          text="Jobs"
          to="/seeker/jobs"
          :badgeNum="receivedAppsCount"
          :isActive="activeIcon === 'jobs'"
          :isMobile="true"
          :aria-current="activeIcon === 'jobs' && 'page'"
        >
          <jobs-icon-filled v-if="activeIcon === 'jobs'" />
          <jobs-icon-outline v-else />
        </NavigationItem>
        <NavigationItem
          text="Explore"
          to="/availability/search"
          :isActive="activeIcon === 'explore'"
          :isMobile="true"
          :aria-current="activeIcon === 'explore' && 'page'"
        >
          <magnifying-glass-icon-filled v-if="activeIcon === 'explore'" />
          <magnifying-glass-icon-outline v-else />
        </NavigationItem>
        <NavigationItem
          v-if="!basicAccount"
          text="Bookings"
          to="/bookings"
          :isActive="activeIcon === 'bookings'"
          :isMobile="true"
          :aria-current="activeIcon === 'bookings' && 'page'"
        >
          <bookings-icon-filled v-if="activeIcon === 'bookings'" />
          <bookings-icon-outline v-else />
        </NavigationItem>
        <button
          id="mobile-account-menu-btn"
          class="nav-icon-group mobile h-full justify-center flex flex-col items-center"
          tabindex="0"
          :aria-expanded="isModalOpen ? 'true' : 'false'"
          @click="toggleModal"
        >
          <Avatar :name="`${account.firstName} ${account.lastName}`" :imageUrl="avatarUrl" />
          <p class="text-caption truncate-name">{{ account.firstName }}</p>
        </button>
      </nav>
    </div>
  </div>
</template>

<script lang="ts">
// Lib
import startCase from 'lodash/startCase';
import { sendSegmentEvent } from '@/lib/segment';

// Icons
import HomeIconFilled from '@/icons/global_nav/HomeIconFilled.vue';
import HomeIconOutline from '@/icons/global_nav/HomeIconOutline.vue';
import JobsIconFilled from '@/icons/global_nav/JobsIconFilled.vue';
import JobsIconOutline from '@/icons/global_nav/JobsIconOutline.vue';
import MagnifyingGlassIconFilled from '@/icons/global_nav/MagnifyingGlassIconFilled.vue';
import MagnifyingGlassIconOutline from '@/icons/global_nav/MagnifyingGlassIconOutline.vue';
import MessagesIconFilled from '@/icons/global_nav/MessagesIconFilled.vue';
import MessagesIconOutline from '@/icons/global_nav/MessagesIconOutline.vue';
import BookingsIconFilled from '@/icons/global_nav/BookingsIconFilled.vue';
import BookingsIconOutline from '@/icons/global_nav/BookingsIconOutline.vue';
import CaretIcon from '@/icons/global_nav/CaretIcon.vue';

// Components
import Avatar from '../avatar/Avatar.vue';
import Badge from '../badge/Badge.vue';
import Button from '../button/Button.vue';
import GlobalNavDropdown from './GlobalNavDropdown.vue';
import NavigationLogo from './NavigationLogo.vue';
import NavigationItem from './NavigationItem.vue';
import NavigationMobileTop from './NavigationMobileTop.vue';

export default {
  name: 'GlobalNav',
  components: {
    // Icons
    HomeIconFilled,
    HomeIconOutline,
    JobsIconFilled,
    JobsIconOutline,
    MagnifyingGlassIconOutline,
    MagnifyingGlassIconFilled,
    MessagesIconFilled,
    MessagesIconOutline,
    BookingsIconFilled,
    BookingsIconOutline,
    CaretIcon,

    // Components
    Avatar,
    Badge,
    Button,
    GlobalNavDropdown,
    NavigationLogo,
    NavigationItem,
    NavigationMobileTop,
  },
  data(): any {
    return {
      isModalOpen: false,
      handleEscapeKey: null as any,
    };
  },
  computed: {
    preventRedirectInRegistration(): boolean {
      return (
        this.$route.path.startsWith('/registration') ||
        this.$route.path.startsWith('/corporate-onboarding') ||
        this.$route.query.source === 'corporate-onboarding' ||
        this.$route.query.source === 'registration'
      );
    },
    unreadMessageCount(): number {
      return this.$store.getters['navigation/unreadMessageCount'];
    },
    avatarUrl(): string {
      return this.account?.croppedPhoto || this.account?.originalPhoto || '';
    },
    account() {
      return this.$store.getters['account/getAccount'];
    },
    receivedAppsCount(): number {
      return this.$store.getters['navigation/receivedAppsCount'];
    },
    activeIcon() {
      const path = this.$route?.path ?? window.location.pathname;
      if (path === '/account/seekers/dashboard') return 'home';
      if (path.startsWith('/seeker/jobs')) return 'jobs';
      if (path.startsWith('/messages')) return 'messages';
      if (path === '/bookings' || path.startsWith('/bookings/')) return 'bookings';
      if (path === '/profile/provider/search' || path === '/availability/search') return 'explore';
      return '';
    },
    basicAccount(): boolean {
      return this.account?.isBasic();
    },
    accountType(): string {
      return startCase(this.account.accountType());
    },
    isSimpleHeader(): boolean {
      return this.$route.matched.some((r) => r.meta?.simpleHeader);
    },
    isMessagesRoute(): boolean {
      return this.$route.path.startsWith('/messages');
    },
    messagesMobileLinks() {
      const links: Array<{ href: string; label: string; badgeCount?: number; external?: boolean }> = [
        { href: '/account/seekers/dashboard', label: 'Home' },
        {
          href: '/seeker/my-jobs/opened',
          label: 'Jobs',
          badgeCount: this.receivedAppsCount,
        },
        { href: '/availability/search', label: 'Explore' },
      ];
      if (!this.basicAccount) {
        links.push({ href: '/bookings', label: 'Bookings' });
      }
      links.push({ href: '/account/settings', label: 'Account' });
      if (this.basicAccount) {
        links.push({ href: '/upgrade/pricing', label: 'Premium pricing' });
      }
      links.push(
        { href: 'https://www.sittercity.com/trust-and-safety', label: 'Trust & Safety', external: true },
        { href: 'https://support.sittercity.com/hc/en-us', label: 'Help', external: true },
        { href: '/logout', label: 'Log out' }
      );
      return links;
    },
  },
  watch: {
    isModalOpen(val) {
      if (val) {
        const dropdown = document.getElementById('global-nav-dropdown');
        this.$nextTick(() => {
          if (dropdown) {
            dropdown.focus();
          }
        });
      }
    },
  },
  created() {
    this.handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.isModalOpen = false;
      }
    };
    document.addEventListener('keydown', this.handleEscapeKey);
  },
  mounted() {
    this.$store.dispatch('navigation/startPolling');
  },
  beforeDestroy() {
    this.$store.dispatch('navigation/stopPolling');
    if (this.handleEscapeKey) {
      document.removeEventListener('keydown', this.handleEscapeKey);
    }
  },
  methods: {
    toggleModal() {
      this.isModalOpen = !this.isModalOpen;
    },
    handleClickOutside(target) {
      const desktopButton = document.getElementById('desktop-account-menu-btn');
      const mobileButton = document.getElementById('mobile-account-menu-btn');
      if (this.isModalOpen) {
        if (!(desktopButton?.contains(target) || mobileButton?.contains(target))) {
          this.isModalOpen = false;
        }
      }
    },
    upgradeClicked() {
      sendSegmentEvent('GlobalNav_UpgradePricing', {});
    },
  },
};
</script>

<style lang="scss">
.global-navigation {
  z-index: 100;
  position: fixed;
  top: 0;
  width: 100%;
  background: var(--light-surface-foreground, #fff);
  display: flex;
  align-items: center;
  height: 48px;
  border-bottom: none;
  box-shadow: 0 2px 12px rgba(215, 216, 219, 0.9);

  @media only screen and (min-width: 768px) {
    height: 80px;
    border-bottom: 1px solid #d7d8d9;
    box-shadow: none;
  }

  .icon-container {
    gap: 12px;
    @media only screen and (min-width: 768px) {
      display: flex;
    }
    @media only screen and (min-width: 1024px) {
      gap: 32px;
    }
  }
}

.icon-container-mobile-bottom {
  z-index: 100;
  justify-content: space-around;
  height: 80px;
  width: 100vw;
  position: fixed;
  bottom: 0;
  background-color: var(--light-surface-foreground, #fff);
  @media only screen and (min-width: 768px) {
    visibility: hidden;
  }
  .nav-icon-group {
    width: 72px;
  }
}

.upside-down-icon {
  transform: rotate(180deg);
}

.truncate-name {
  max-width: 64px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  display: inline-block;
}

.mobile-messaging-icon {
  @media only screen and (min-width: 768px) {
    display: none;
  }
  &:focus {
    outline: 2px dotted #bb0049;
  }
  &:hover {
    cursor: pointer;
    path {
      fill: #bb0049;
    }
  }
}

// this is for the cookie banner icon which overlaps mobile bottom nav otherwise
#ot-sdk-btn-floating.ot-floating-button {
  bottom: 85px !important;
  z-index: 51 !important;
  @media only screen and (min-width: 768px) {
    bottom: 10px !important;
  }
}

// this is for the zendesk support icon which overlaps mobile bottom nav otherwise
@media (max-width: 767px) {
  #launcher {
    margin-bottom: 85px !important;
  }
}
</style>
